// sdcard playback query client (design spec §5.1/§5.2/§5.4/§5.5).
//
// The "sd-card client" is the App acting over the op:get bridge member
// PlaybackQuery (`0x2100008050000800`, kind STAT raw) — common/04 §4.8. It is the
// ONLY parameterized-query-over-op:get member (§5.2 ratified exception): the
// request carries a `rawVal` envelope `{q,t1,t2,event_ts?,day?,cursor?}` and the
// op:report reply carries a `rawVal`/`result` ARRAY plus an optional `next_cursor`.
//
// Transport = the EXISTING signaling singleton ws.ts sendCmd; ws.ts is NOT changed.
// sendCmd builds {type:'cmd', device_id, op:'get', id, ...extra} and correlates by
// the injected `request_id`, rejecting with SignalingTimeoutError after 30s.
//
// TRI-STATE (§9.3): a resolved reply with a non-empty result → ANSWERED; resolved
// with an empty result → EMPTY; a SignalingTimeoutError (30s) or an error frame →
// UNREACHABLE. UNREACHABLE MUST NOT be downgraded to "cloud" by callers (that is
// the false-CLOUD bug the spec guards against). `resource_unavailable` is surfaced
// as a retryable transient WITHOUT polluting the tri-state.
//
// PAGINATION (§5.2/§9.10) is MANDATORY: the device `result_line` buffer (~2KB)
// silently truncates large arrays, so every q that can page returns a bounded
// array + `next_cursor`; pbQuery loops with `cursor:<next_cursor>` until the cursor
// is exhausted (bounded by MAX_PAGES to defend against a misbehaving device).

import { SignalingTimeoutError, type SignalFrame, type SignalingClient } from '../ws';
import { signaling as defaultSignaling } from '../ws';

/** The PlaybackQuery member id (common/04 §4.8; device `loop.c:70`). */
export const PLAYBACK_QUERY_ID = '0x2100008050000800';

/** Hard ceiling on cursor-loop iterations (defends against a non-terminating device).
 * range-events pages are small (~6 entries), so a busy multi-hour window needs many
 * pages to reach the live edge; 256 covers a ~6h segment on a chatty device while
 * still bounding a runaway. Sparse days short-circuit early (no next_cursor). */
export const MAX_PAGES = 256;

/** Sub-query vocabulary (design spec §5.4). The Phase-3 rename is authoritative. */
export type PbQueryKind =
  | 'range-events'
  | 'range-clip-existence'
  | 'day-ranges'
  | 'day-list'
  | 'snapshot';

/** Tri-state outcome of a sdcard query (§9.3). */
export type SdcardState = 'ANSWERED' | 'UNREACHABLE' | 'EMPTY';

// ---- result item shapes (design spec §5.5) ----

/** One EVENT-MARKER, keyed by `eid` (q=range-events). */
export interface PbEvent {
  /** 32-lowercase-hex opaque event id minted by signal-agent (§3.2). */
  eid: string;
  /** full-µs epoch of the event marker. */
  event_ts_us: number;
  /** clip window start/end (µs) — the marker's playable window. */
  clip_start_us: number;
  clip_end_us: number;
  /** capability-encoded firmware event id hex (e.g. "0x1100008041000001"). */
  event_id_hex?: string;
  has_snapshot: boolean;
  record_location: string;
  record_mode?: string;
  record_quality?: string;
}

/** A coalesced clip interval (q=day-ranges). */
export interface PbRange {
  start_us: number;
  end_us: number;
}

/** A UTC+0 day that has clips (q=day-list). */
export interface PbDay {
  /** floor(ts_us / 86_400_000_000), UTC+0. */
  day_index: number;
  day_start_us: number;
  day_end_us: number;
}

/** A snapshot locator (q=snapshot); bytes are fetched via §8A /snap/<ts_us>.jpg. */
export interface PbSnapshotLocator {
  ts_us: number;
  duration_us: number;
  len: number;
}

// ---- request params ----

export interface PbQueryParams {
  q: PbQueryKind;
  t1?: number;
  t2?: number;
  event_ts?: number;
  day?: number;
  cursor?: string;
  /** day-ranges: restrict device-side coverage to REC_TYPE_CONTINUOUS media. */
  continuous_only?: boolean;
}

// ---- parsed single-page reply (pure) ----

/** A single op:report page, parsed from the wire frame (pure, unit-tested). */
export interface PbReplyPage<T = unknown> {
  /** Wire `status` (`ok|invalid_payload|resource_unavailable|error`). */
  status: string;
  /** The `result` array for this page (empty when absent). */
  items: T[];
  /** Boolean short-circuit for q=range-clip-existence (undefined for other q). */
  exists?: boolean;
  /** Opaque continuation cursor; absent → no further page. */
  next_cursor?: string;
}

/**
 * Where the device puts the reply payload varies by leg: the signaling bridge
 * echoes the device op:report inside the wire frame's `rawVal`, but a flat frame
 * (or a `payload`-wrapped one) is also tolerated. Returns the first object that
 * looks like a PlaybackQuery reply (`status` present).
 */
function replyBody(frame: SignalFrame): Record<string, unknown> {
  const candidates: unknown[] = [
    (frame as { rawVal?: unknown }).rawVal,
    frame.payload,
    frame,
  ];
  for (const c of candidates) {
    if (c && typeof c === 'object' && 'status' in (c as object)) return c as Record<string, unknown>;
  }
  // No status anywhere: fall back to rawVal/payload/frame in order so callers still
  // get an items array if the device omitted status on a successful page.
  for (const c of candidates) {
    if (c && typeof c === 'object') return c as Record<string, unknown>;
  }
  return {};
}

/**
 * Parse ONE op:report page (pure). Reads `status`, the `result` array, the optional
 * `exists` boolean (range-clip-existence), and `next_cursor`. Defensive about where
 * the body sits (rawVal / payload / flat) and about a non-array `result`.
 */
export function parsePbReply<T = unknown>(frame: SignalFrame): PbReplyPage<T> {
  const body = replyBody(frame);
  const status = typeof body.status === 'string' ? body.status : 'ok';
  const rawResult = body.result;
  const items = Array.isArray(rawResult) ? (rawResult as T[]) : [];
  const next = body.next_cursor;
  const next_cursor = typeof next === 'string' && next !== '' ? next : undefined;
  const exists = typeof body.exists === 'boolean' ? body.exists : undefined;
  return { status, items, exists, next_cursor };
}

// ---- transport-backed query (cursor loop + tri-state) ----

export interface PbQueryResult<T> {
  state: SdcardState;
  items: T[];
  /** True when status=resource_unavailable (retryable; tri-state stays EMPTY). */
  transientUnavailable?: boolean;
  /** range-clip-existence boolean short-circuit (undefined for other q). */
  exists?: boolean;
}

function buildRawVal(params: PbQueryParams, cursor?: string): Record<string, unknown> {
  const rawVal: Record<string, unknown> = { q: params.q };
  if (params.t1 !== undefined) rawVal.t1 = params.t1;
  if (params.t2 !== undefined) rawVal.t2 = params.t2;
  if (params.event_ts !== undefined) rawVal.event_ts = params.event_ts;
  if (params.day !== undefined) rawVal.day = params.day;
  if (params.continuous_only) rawVal.continuous_only = true;
  if (cursor !== undefined) rawVal.cursor = cursor;
  return rawVal;
}

/**
 * Run a PlaybackQuery with MANDATORY cursor pagination and tri-state wrapping.
 *
 * - 30s SignalingTimeoutError (or an error frame / "not connected") → UNREACHABLE
 *   (never default to cloud).
 * - resolved + non-empty across pages → ANSWERED; resolved + empty → EMPTY.
 * - status=resource_unavailable → EMPTY + transientUnavailable:true (retryable; does
 *   NOT pollute the tri-state — §9.3).
 *
 * The cursor loop concatenates pages until `next_cursor` is absent (or MAX_PAGES).
 */
export async function pbQuery<T = unknown>(
  deviceId: string,
  params: PbQueryParams,
  client: SignalingClient = defaultSignaling
): Promise<PbQueryResult<T>> {
  const all: T[] = [];
  let cursor = params.cursor;
  let exists: boolean | undefined;
  let transientUnavailable = false;

  try {
    for (let page = 0; page < MAX_PAGES; page++) {
      const frame = await client.sendCmd(deviceId, 'get', PLAYBACK_QUERY_ID, {
        rawVal: buildRawVal(params, cursor),
      });
      const parsed = parsePbReply<T>(frame);

      if (parsed.status === 'resource_unavailable') {
        // Transient: surface as retryable, do not contaminate the tri-state.
        transientUnavailable = true;
        break;
      }
      if (parsed.status === 'invalid_payload' || parsed.status === 'error') {
        // A definitive negative answer from a REACHABLE device. Treat as EMPTY
        // (the device answered; it just has nothing / rejected the params) rather
        // than UNREACHABLE — UNREACHABLE is reserved for no-answer (§9.3).
        break;
      }

      if (parsed.exists !== undefined) exists = parsed.exists;
      for (const it of parsed.items) all.push(it);

      if (!parsed.next_cursor) break;
      cursor = parsed.next_cursor;
    }
  } catch (err) {
    if (err instanceof SignalingTimeoutError) {
      return { state: 'UNREACHABLE', items: [] };
    }
    // error frame (sendCmd rejects with a tagged Error) or "not connected" — the
    // device did not give a usable answer → UNREACHABLE, NOT cloud.
    return { state: 'UNREACHABLE', items: [] };
  }

  // range-clip-existence answers purely via `exists`; an ANSWERED-but-empty result[]
  // with exists:true is still a positive answer.
  const hasContent = all.length > 0 || exists === true;
  const state: SdcardState = hasContent ? 'ANSWERED' : 'EMPTY';
  return { state, items: all, exists, ...(transientUnavailable ? { transientUnavailable } : {}) };
}

// ---- typed convenience wrappers (one per q) ----

/** A1 / b1 / c7 — per-marker event list for [t1,t2] (cursor-paged). */
export function queryRangeEvents(
  deviceId: string,
  t1: number,
  t2: number,
  client?: SignalingClient
): Promise<PbQueryResult<PbEvent>> {
  return pbQuery<PbEvent>(deviceId, { q: 'range-events', t1, t2 }, client);
}

/** A2 — cheap boolean short-circuit: does a clip exist in [t1,t2]? */
export function queryRangeClipExistence(
  deviceId: string,
  t1: number,
  t2: number,
  client?: SignalingClient
): Promise<PbQueryResult<PbRange>> {
  return pbQuery<PbRange>(deviceId, { q: 'range-clip-existence', t1, t2 }, client);
}

/** c2 — full coalesced interval list for a day window (cursor-paged). */
export function queryDayRanges(
  deviceId: string,
  dayStartUs: number,
  dayEndUs: number,
  continuousOnly = false,
  client?: SignalingClient
): Promise<PbQueryResult<PbRange>> {
  // continuous_only restricts device-side coverage to REC_TYPE_CONTINUOUS media so the
  // continuous timeline excludes event clips (the sparse -5s..+10s windows) + snapshots.
  const params: PbQueryParams = { q: 'day-ranges', t1: dayStartUs, t2: dayEndUs };
  if (continuousOnly) params.continuous_only = true;
  return pbQuery<PbRange>(deviceId, params, client);
}

/** c1 — which UTC+0 days have clips (index-only scan). */
export function queryDayList(
  deviceId: string,
  client?: SignalingClient
): Promise<PbQueryResult<PbDay>> {
  return pbQuery<PbDay>(deviceId, { q: 'day-list' }, client);
}

/** b1 snapshot locator for a marker (bytes via §8A /snap/<ts_us>.jpg). */
export function querySnapshot(
  deviceId: string,
  eventTsUs: number,
  client?: SignalingClient
): Promise<PbQueryResult<PbSnapshotLocator>> {
  return pbQuery<PbSnapshotLocator>(deviceId, { q: 'snapshot', event_ts: eventTsUs }, client);
}
